import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adminApi, fileUrl } from "../../lib/api";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader } from "./Dashboard";
import { CharacterProfileDisplay, hasProfileContent } from "../../components/app/CharacterProfile";
import { SkullRating } from "../../components/app/SkullRating";
import { Printer, Pencil, ArrowLeft, MapPin, User, ImageIcon } from "lucide-react";

export default function RoleDetail() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const { currentEvent, isArchived } = useEvents();
  const [role, setRole] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/roles/${rid}`).then(({ data }) => setRole(data)).catch(() => setError(true));
  }, [rid]);

  if (error) return <AdminLayout title="Role"><p className="text-sm text-[#6B6862]">Role not found in this event.</p></AdminLayout>;
  if (!role) return <AdminLayout title="Character Profile"><Loader /></AdminLayout>;

  const imgs = role.reference_images || [];

  return (
    <AdminLayout
      title="Character Profile"
      subtitle={role.name}
      actions={
        <div className="flex items-center gap-2 no-print">
          <button onClick={() => navigate("/admin/roles")} data-testid="role-back-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood"><ArrowLeft size={14} /> Back</button>
          {!isArchived && <Link to={`/admin/roles?edit=${role.id}`} data-testid="role-edit-link" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood"><Pencil size={14} /> Edit</Link>}
          <button onClick={() => window.print()} data-testid="role-print-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Printer size={14} /> Print / Export PDF</button>
        </div>
      }
    >
      <div className="print-area max-w-3xl">
        {/* Sheet header */}
        <div className="border-b-2 border-blood pb-4 mb-6">
          <div className="label-mini">{currentEvent?.name || "Character Sheet"}</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight mt-1">{role.name}</h1>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#A19D94]">
            {role.placement && <span className="flex items-center gap-1"><MapPin size={13} className="text-blood" /> {role.placement}</span>}
            {role.character_name && <span className="flex items-center gap-1"><User size={13} className="text-blood" /> {role.character_name}</span>}
          </div>
          {role.skill_level > 0 && <SkullRating value={role.skill_level} size={20} className="mt-3" />}
        </div>

        {/* Character profile */}
        {hasProfileContent(role) ? (
          <CharacterProfileDisplay role={role} />
        ) : (
          <p className="text-sm text-[#6B6862] italic no-print">No character profile built yet. Use Edit to add profile fields.</p>
        )}

        {/* Reference images */}
        {imgs.length > 0 && (
          <div className="mt-6">
            <h4 className="font-display text-sm uppercase tracking-wider text-blood mb-2 flex items-center gap-2"><ImageIcon size={14} /> Reference Images</h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {imgs.map((id) => (
                <img key={id} src={fileUrl(id)} alt="reference" data-testid={`role-detail-img-${id}`} className="w-full aspect-square object-cover border border-[#262626]" />
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
